import { useEffect, useState } from 'react'

import { Tooltip as ReactTooltip } from 'react-tooltip'

import {
  Box,
  Button,
  Flex,
  Modal,
} from 'app/components/design-system-components'
import { fontSizes } from 'app/components/design-system-components/styles/variables'
import { Text } from 'app/components/design-system-components/typography'
import UpgradeCTA from 'app/components/design-system-components/UpgradeCTA'
import { theme } from 'app/match/applicationReportPDF/assets/theme'
import { getTeamSettings } from 'app/services/http/teams/settings'
import * as snugNotifier from 'app/services/snugNotifier'
import { parseDataUtil } from 'app/sm/helpers'
import {
  deleteAgencyPod,
  getAgencyListPods,
  postAgencyPod,
  putAgencyPod,
} from 'app/sm/team_viewing_preference/access'
import { AddPodModal } from 'app/sm/team_viewing_preference/pods/AddPodModal'
import {
  MAX_AGENCY_PODS,
  Pod,
} from 'app/sm/team_viewing_preference/pods/helper'
import UserNotAuthorizedShowTeamMembersList from 'app/sm/team_viewing_preference/userNotAuthorized/showTeamMembersList'
import { isBefore } from 'app/utils/datetime/helpers'

type IPodsContainerProps = {
  teamGUID: string
  isAdmin: boolean
  teamSlug: string
  agencyGUID: string
  currentUserEmail: string
}

const PodItem = ({
  pod,
  isAdmin,
  openEditPodModal,
  toggleDeletePodModal,
}: {
  pod: Pod
  isAdmin: boolean
  openEditPodModal: (isEditMode: boolean, pod: Pod) => void
  toggleDeletePodModal: (pod: Pod) => void
}) => {
  const teamMembersTooltipID = `team_members_tooltip_id_${pod.id}`
  const teamMembersTooltipContent = (
    <Flex flexDirection="column" justifyContent="center">
      {pod.managers?.map((manager) => (
        <Text
          key={manager.guidID}
          as="span"
        >{`${manager?.firstName} ${manager?.lastName}`}</Text>
      ))}
    </Flex>
  )
  const firstManagerName =
    pod.managers?.length > 0 &&
    `${pod.managers?.[0]?.firstName} ${pod.managers?.[0]?.lastName}`
  const agencyPodLabel =
    pod.managers?.length > 1
      ? `${firstManagerName}  + ${pod.managers?.length - 1} more`
      : firstManagerName

  return (
    <Box mb={theme.space[4] + 'px'}>
      <Text as="span" mr={theme.space[3] + 'px'} color={theme.colors.gray500}>
        {pod.name}
        {agencyPodLabel && ':'}
      </Text>
      <Text
        as="span"
        mr={theme.space[3] + 'px'}
        fontWeight={theme.fontWeights.bold}
        data-tooltip-id={teamMembersTooltipID}
      >
        {agencyPodLabel}
      </Text>
      <ReactTooltip className="tool-tip-style" id={teamMembersTooltipID}>
        {teamMembersTooltipContent}
      </ReactTooltip>
      {isAdmin && (
        <>
          <Button
            style={{
              fontSize: theme.fontSizes.pLarge16,
            }}
            variant="linkBlueUnderlinedWithHoverBold"
            mr={theme.space[3] + 'px'}
            onClick={openEditPodModal.bind(null, true, pod)}
          >
            edit
          </Button>
          <Button
            style={{
              fontSize: theme.fontSizes.pLarge16,
            }}
            variant="linkBlueUnderlinedWithHoverBold"
            onClick={toggleDeletePodModal.bind(null, pod)}
          >
            remove
          </Button>
        </>
      )}
    </Box>
  )
}

function PodsContainer({
  teamGUID,
  isAdmin,
  teamSlug,
  agencyGUID,
}: IPodsContainerProps) {
  const [isAddPodModalOpen, setIsAddPodModalOpen] = useState(false)
  const [isDeletePodModalOpen, setIsDeletePodModalOpen] = useState(false)
  const [isEditmode, setIsEditMode] = useState(false)
  const [editingPod, setEditingPod] = useState<Pod>()
  const [listPods, setListPods] = useState<Pod[]>([])
  const [groupBundleEnabled, setGroupBundleEnabled] = useState(false)

  useEffect(() => {
    if (agencyGUID) {
      Promise.all([
        getTeamSettings(agencyGUID, {
          keys: ['bundle'],
        }),
        getAgencyListPods(agencyGUID),
      ])
        .then(
          ([{ settingInfo }, podsList]: [
            setting: { settingInfo: any },
            podsList: Pod[],
          ]) => {
            setListPods(
              podsList.sort((a, b) =>
                isBefore(new Date(b.created_at), new Date(a.created_at))
                  ? -1
                  : 1,
              ),
            )
            const { bundle } = settingInfo
            const bundles =
              parseDataUtil.convertObjValueFromStringToBoolean(bundle)

            setGroupBundleEnabled((bundles as any).group_bundle)
          },
        )
        .catch((err) => snugNotifier.error(err))
    }
  }, [agencyGUID])

  const toggleAddPodModal = () => {
    setIsAddPodModalOpen((prev) => !prev)
    setIsEditMode(false)
    setEditingPod(undefined)
  }

  const openEditPodModal = (isEditMode: boolean, pod: Pod) => {
    setIsAddPodModalOpen((prev) => !prev)
    setIsEditMode(isEditMode)
    setEditingPod(pod)
  }

  const handleAddEditPod = ({
    name,
    managers,
  }: {
    name: string
    managers: string[]
  }) => {
    if (!name.trim()) {
      snugNotifier.error('Pod name is required')
      return
    }

    if (!managers.length) {
      snugNotifier.error('At least one manager is required')
      return
    }

    if (isEditmode && editingPod) {
      putAgencyPod(agencyGUID, editingPod?.id, { name, managers: managers })
        .then((data: Pod) => {
          setListPods((prev) =>
            prev.map((item) => (item.id === editingPod?.id ? data : item)),
          )
          toggleAddPodModal()
          snugNotifier.success('Edit pod successful')
        })
        .catch((err: any) => {
          console.log(err)
          snugNotifier.error(err.message)
        })
    } else {
      if (listPods.length >= MAX_AGENCY_PODS) {
        return snugNotifier.error('Maximum number of pods reached for agency')
      }

      postAgencyPod(agencyGUID, { name, managers: managers })
        .then((data: Pod) => {
          setListPods((prev) => [...prev, data])
          toggleAddPodModal()
          snugNotifier.success('Add pod successful')
        })
        .catch((err: any) => {
          snugNotifier.error(err.message)
        })
    }
  }

  const toggleDeletePodModal = (pod: Pod) => {
    setIsDeletePodModalOpen(true)
    setEditingPod(pod)
  }

  const handleDeletePod = (podID?: string) => {
    deleteAgencyPod(agencyGUID, podID)
      .then(() => {
        setListPods(listPods.filter((pod) => pod.id !== podID))
        snugNotifier.success('Delete pod successful')
      })
      .catch((err: any) => {
        console.log(err)
        snugNotifier.error(err)
      })
  }

  return (
    <Box>
      <Text as="h1" mb={4} mt={0} fontSize={fontSizes[12] + 'px'}>
        Pods
      </Text>
      <Box mb={6} fontSize={5}>
        Group team members for easy filtering across viewings, applications and
        activity reporting.{' '}
        <a
          href="https://help.snug.com/hc/en-us/articles/12033322248591"
          target="_blank"
          rel="noopener noreferrer"
        >
          learn more
        </a>
      </Box>

      {groupBundleEnabled && !isAdmin && (
        <UserNotAuthorizedShowTeamMembersList teamSlug={teamSlug} />
      )}

      {groupBundleEnabled ? (
        <Box mt={theme.space[8] + 'px'} mb={theme.space[8] + 'px'}>
          <Box mb={theme.space[5] + 'px'}>
            {listPods.length > 0 ? (
              listPods.map((pod) => (
                <PodItem
                  pod={pod}
                  isAdmin={isAdmin}
                  openEditPodModal={openEditPodModal}
                  toggleDeletePodModal={toggleDeletePodModal}
                />
              ))
            ) : (
              <Text mt={0}>There are no pods set up</Text>
            )}
          </Box>

          {isAdmin && <Button onClick={toggleAddPodModal}>Add a pod</Button>}
        </Box>
      ) : (
        <UpgradeCTA
          alignRight={false}
          availableOn="Group Bundle"
          availableText="This feature is available on the"
          maxWidth="100%"
          textContainerProps={{
            display: 'flex',
            alignItems: 'center',
            lineHeight: '1.3',
          }}
          learnmoreVariant="linkBlueWithoutWeight"
        />
      )}

      {isAddPodModalOpen && (
        <AddPodModal
          teamID={teamGUID}
          editingPod={editingPod}
          onDismissClick={toggleAddPodModal}
          isEditmode={isEditmode}
          onSubmitForm={handleAddEditPod}
        />
      )}

      {isDeletePodModalOpen && (
        <Modal
          modalHeading="Are you sure?"
          modalBody={
            <Box style={{ lineHeight: theme.lineHeights.encrption }} mt={3}>
              This pod will no longer be available. Existing team member
              assignments will not be affected. This cannot be undone.
            </Box>
          }
          primaryLabel="Remove"
          secondaryLabel="Cancel"
          primaryAction={() => {
            handleDeletePod(editingPod?.id)
            setIsDeletePodModalOpen(false)
          }}
          secondaryAction={() => setIsDeletePodModalOpen(false)}
          toggleModal={() => setIsDeletePodModalOpen(false)}
          modalFooter={undefined}
        />
      )}
    </Box>
  )
}

export default PodsContainer
